import { PermissionChoose } from "@/constants/share";
import { IdentityType } from "@/constants/user";
import { updateShareSetting } from "@/services/share";
import { getShareLink } from "@/services/summary";
import userInfoStore from "@/store/user.store";
import summaryCommonStore from "@/store/summaryCommon.store";
import { IPartialShareSettingAttr, IShareSettingAttr } from "@/types/share";
import { IShareLinkInfo, ICreateShareParams } from "@/types/summary";
import { useState, useEffect } from "react";
import useGetPermission from "./useGetPermission";
import useGetSharerList from "./useGetSharerList";
import { message } from "antd";

const generateSixDigitNumber = () => {
  // 生成一个 100000 到 999999 之间的随机整数
  return Math.floor(100000 + Math.random() * 900000);
};

const useGetShareSetting = (streamName: string) => {
  const [shareInfo, setShareInfo] = useState<IShareLinkInfo | null>(null);
  const [panel, setPanel] = useState("default");

  const { editable, mySharePermission, fetchMeetingPermission } =
    useGetPermission(streamName);
  const { sharerList, setSharerList, inviteMember, refreshSharerList, removeShareMember, updateShareMenber } =
    useGetSharerList(shareInfo?.shareId ?? "");

  const [shareSetting, setShareSetting] = useState<IShareSettingAttr>({
    shareScope: undefined,
    password: "",
    linkValidity: undefined,
    allowBackUp: false,
    allowExport: false,
    waterMarkEnable: false,
  });
  const company = userInfoStore((state) => state.company);

  const isPassWord =
    shareSetting.shareScope === PermissionChoose.Password ||
    shareSetting.shareScope === PermissionChoose.StaffPassword;

  const createShareLink = () => {
    const params: ICreateShareParams = {
      streamName,
      platform: "IOS",
      password: isPassWord ? shareSetting.password : undefined,
    };
    if (!params.password) {
      delete params.password;
    }
    console.log("createShareLink", params);
    getShareLink(params).then((res) => {
      setShareInfo(res.data);
      const {
        shareScope,
        password,
        linkValidity,
        allowBackUp,
        allowExport,
        waterMarkEnable,
      } = res.data;
      setShareSetting({
        shareScope,
        password: password || `${generateSixDigitNumber()}`,
        linkValidity,
        allowBackUp,
        allowExport,
        waterMarkEnable,
      });
    }).catch((error: any) => {
      // 录制信息已删除
      if (error?.errCode === 40002103) {
        // 由于此hook在分享弹窗中使用,当记录已删除时应该关闭弹窗并显示已删除UI
        // 但这里无法直接访问summaryCommonStore,所以通过handleError传递错误
        handleError(error);
      }
    });
  };
  const onShareSettingChange = async (newParams: IPartialShareSettingAttr) => {
    console.log("onShareSettingChange", newParams);
    try {
      await updateShareSetting({
        shareId: shareInfo?.shareId || "",
        ...newParams,
      });
      // 当选择分享范围为企业内时，需要更新共享人列表，移除非本企业的人
      if (
        newParams.shareScope === PermissionChoose.StaffCheck ||
        newParams.shareScope === PermissionChoose.StaffPassword
      ) {
        refreshSharerList();
      }
      // 当选择链接有效期时，需要更新链接有效期
      if (newParams.linkValidity) {
        const res = await getShareLink({ streamName, platform: "IOS" });
        console.log("getShareLink", res);
        setShareInfo(res.data);
      }
      setShareSetting(
        shareSetting ? { ...shareSetting, ...newParams } : shareSetting
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      handleError(error);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleError = (error: any) => {
    // 录制信息已删除
    if (error.errCode === 40002103) {
      summaryCommonStore.getState().setErrorStatus(40002103);
      return;
    }
    const noPermissionList = [40002113, 40002143];
    if (noPermissionList.includes(error.errCode)) {
      message.error(`权限发生变更,${error.errMsg}`);
      setPanel("default");
      fetchMeetingPermission();
      refreshSharerList();
    }
  };

  const updateShareScope = (shareScope: PermissionChoose) => {
    const params: IPartialShareSettingAttr = {
      shareScope,
    };
    if (
      shareScope === PermissionChoose.Password ||
      shareScope === PermissionChoose.StaffPassword
    ) {
      params.password = shareSetting.password;
    }
    onShareSettingChange(params);
  };

  const updatePassword = (password: string) => {
    onShareSettingChange({
      password,
      shareScope: shareSetting.shareScope,
    });
  };

  useEffect(() => {
    if (!streamName) return;
    createShareLink();
    return () => {
      setShareInfo(null);
    };
  }, [streamName]);

  // 当前登录账号类型为企业/个人
  const isCompany = company.identityType === IdentityType.company;

  return {
    editable,
    isCompany,
    isPassWord,
    shareSetting,
    shareInfo,
    mySharePermission,
    updateShareScope,
    updatePassword,
    onShareSettingChange,
    handleError,
    panel,
    setPanel,
    sharerList,
    inviteMember,
    setSharerList,
    refreshSharerList,
    removeShareMember,
    updateShareMenber
  };
};

export default useGetShareSetting;
